import { useState, useMemo } from "react";
import { Card, CardContent } from "@/shared/ui/card";
import { Input } from "@/shared/ui/input";
import { Badge } from "@/shared/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/ui/select";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/shared/ui/collapsible";
import { Pencil, Plus, Search, Loader2, Check, ChevronRight, ImageIcon, Trash2, Trash, RotateCcw, Tag, QrCode } from "lucide-react";
import { Button } from "@/shared/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/shared/ui/alert-dialog";
import { AddAssetDialog } from "./AddAssetDialog";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/shared/integrations/supabase/client";
import { useToast } from "@/shared/hooks/use-toast";
import {
  usePropertySurveyAssets,
  type SurveyAssetPhoto,
} from "./hooks/usePropertySurveyAssets";
import {
  PhotoViewerModal,
  type PhotoViewerPhoto,
  type PhotoViewerTag,
} from "@/shared/ui/PhotoViewerModal";
import { useThumbnailUrls } from "@/shared/hooks/useThumbnailUrls";
import { storageErrorMessage } from "@/shared/lib/storageErrorMessage";
import { rotateAssetPhoto } from "../services/rotateAssetPhoto";
import { setAssetPhotoTag } from "../services/setAssetPhotoTag";
import { setAssetPhotoUrl } from "../services/setAssetPhotoUrl";
import { deleteAssetPhoto, restoreAssetPhoto } from "../services/deleteAssetPhoto";
import {
  isNameplateCategory,
  isQrCodeCategory,
  toggleTag,
} from "../services/assetPhotoTags.logic";
import { FirebaseImage } from "@/shared/components/FirebaseImage";
import { cn } from "@/shared/lib/utils";
import {
  useEditablePropertyAssets,
  type EditableAsset,
} from "./hooks/useEditablePropertyAssets";
import { useRecycledPropertyAssets } from "./hooks/useRecycledPropertyAssets";
import { useRecycledAssetPhotos } from "./hooks/useRecycledAssetPhotos";
import { mapRecycledRow } from "./hooks/recycledAssets.logic";
import {
  useAssetCategories,
  type AssetCategoryOption,
} from "./hooks/useAssetCategories";
import type { TablesUpdate } from "@/shared/integrations/supabase/types";

type FieldStatus = "idle" | "saving" | "success" | "error";

function StatusIndicator({ status }: { status: FieldStatus }) {
  if (status === "saving")
    return <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />;
  if (status === "success")
    return <Check className="h-3 w-3 text-emerald-500" />;
  return null;
}

function FieldCell({
  status,
  children,
}: {
  status: FieldStatus;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-center gap-1.5">
      <div className="flex-1 min-w-0">{children}</div>
      <span className="w-3 flex-shrink-0">
        <StatusIndicator status={status} />
      </span>
    </div>
  );
}

function AssetPhotoStrip({
  photos,
  propertyId,
}: {
  photos: SurveyAssetPhoto[];
  propertyId: string;
}) {
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerIndex, setViewerIndex] = useState(0);
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // These photos are full-resolution originals (~2.4 MB each); render the stored
  // 250 px thumbnails instead and fall back per-photo when there isn't one.
  const photoUrls = useMemo(
    () => photos.map((p) => p.signedUrl as string).filter(Boolean),
    [photos],
  );
  const { data: thumbnails } = useThumbnailUrls(photoUrls);
  const thumbFor = (url: string) => thumbnails?.get(url) ?? url;

  const viewerPhotos = useMemo<PhotoViewerPhoto[]>(
    () =>
      photos.map((p) => ({
        url: p.signedUrl as string,
        thumbnailUrl: thumbnails?.get(p.signedUrl as string),
        id: p.id,
        isNameplate: isNameplateCategory(p.category),
        isQrCode: isQrCodeCategory(p.category),
        // The entry synthesised from property_assets.asset_photo_url has no
        // asset_documents row, so there is nothing to tag or delete.
        hasDocumentRow: p.hasDocumentRow !== false,
      })),
    [photos, thumbnails],
  );

  const photoByViewerKey = useMemo(() => {
    const m = new Map<string, SurveyAssetPhoto>();
    for (const p of photos) m.set(p.id, p);
    return m;
  }, [photos]);

  const handleRotateSave = async (
    viewerPhoto: PhotoViewerPhoto,
    degrees: number,
    onProgress: (percent: number) => void,
  ) => {
    const photo = viewerPhoto.id ? photoByViewerKey.get(viewerPhoto.id) : undefined;
    const url = photo?.signedUrl ?? viewerPhoto.url;
    try {
      const { downloadUrl } = await rotateAssetPhoto({ url, degrees, onProgress });
      // The overwrite re-minted the download token, so the stored URL now 403s.
      // Persist the new one before anything re-reads it.
      if (photo) {
        await setAssetPhotoUrl({
          photoId: photo.hasDocumentRow === false ? undefined : photo.id,
          assetId: photo.asset_id,
          previousUrl: photo.file_path,
          nextUrl: downloadUrl,
        });
      }
      toast({ title: "Rotation saved" });
      // Re-read the photos, and drop the thumbnail map since the `_th.jpg`
      // object was rewritten and its token re-minted too.
      await queryClient.invalidateQueries({ queryKey: ["survey-assets", propertyId] });
      await queryClient.invalidateQueries({ queryKey: ["thumbnail-urls"] });
    } catch (err) {
      console.error("rotateAssetPhoto failed", err);
      toast({
        title: "Failed to save rotation",
        description: storageErrorMessage(err),
        variant: "destructive",
      });
      throw err;
    }
  };

  const handleDeletePhoto = async (viewerPhoto: PhotoViewerPhoto) => {
    const photo = viewerPhoto.id ? photoByViewerKey.get(viewerPhoto.id) : undefined;
    if (!photo || photo.hasDocumentRow === false) return;
    try {
      await deleteAssetPhoto({
        photoId: photo.id,
        assetId: photo.asset_id,
        filePath: photo.file_path,
      });
      toast({ title: "Photo moved to recycle bin" });
      await queryClient.invalidateQueries({ queryKey: ["survey-assets", propertyId] });
      await queryClient.invalidateQueries({ queryKey: ["recycled-asset-photos", propertyId] });
    } catch (err) {
      toast({
        title: "Failed to delete photo",
        description: (err as Error).message,
        variant: "destructive",
      });
      throw err;
    }
  };

  const handleSetTag = async (viewerPhoto: PhotoViewerPhoto, tag: PhotoViewerTag) => {
    const photo = viewerPhoto.id ? photoByViewerKey.get(viewerPhoto.id) : undefined;
    if (!photo || photo.hasDocumentRow === false) return;

    const clicked = tag === "nameplate" ? "tag" : "qr_code";
    const next = toggleTag(photo.category, clicked);

    try {
      await setAssetPhotoTag({
        photoId: photo.id,
        assetId: photo.asset_id,
        filePath: photo.file_path,
        tag: next,
      });
      toast({
        title:
          next === "tag"
            ? "Tagged as nameplate"
            : next === "qr_code"
              ? "Tagged as QR code"
              : "Tag removed",
      });
      await queryClient.invalidateQueries({ queryKey: ["survey-assets", propertyId] });
    } catch (err) {
      toast({
        title: "Failed to update tag",
        description: (err as Error).message,
        variant: "destructive",
      });
    }
  };

  if (photos.length === 0) {
    return (
      <div className="flex items-center gap-2 px-3 py-3 text-xs text-muted-foreground">
        <ImageIcon className="h-3 w-3" />
        No photos for this asset.
      </div>
    );
  }

  return (
    <div className="px-3 py-3">
      <div className="flex flex-wrap gap-2">
        {photos.map((photo, index) => {
          const isNameplate = isNameplateCategory(photo.category);
          const isQrCode = isQrCodeCategory(photo.category);
          const tagLabel = isNameplate ? "Nameplate" : isQrCode ? "QR code" : null;
          return (
            <button
              key={photo.id}
              type="button"
              onClick={() => {
                setViewerIndex(index);
                setViewerOpen(true);
              }}
              className="relative h-16 w-16 overflow-hidden rounded-md border border-border/60 transition-all hover:border-primary hover:ring-2 hover:ring-primary/30 focus:outline-none focus-visible:border-primary focus-visible:ring-2 focus-visible:ring-primary/30"
              aria-label={
                tagLabel
                  ? `View photo ${index + 1} of ${photos.length} (${tagLabel})`
                  : `View photo ${index + 1} of ${photos.length}`
              }
            >
              <FirebaseImage
                src={thumbFor(photo.signedUrl as string)}
                alt={photo.document_name || `Photo ${index + 1}`}
                loading="lazy"
                className="h-full w-full object-cover"
              />
              {/* Tag badge, mirroring the forms photo grid in robotic-imaging-web.
                  category is a single column, so at most one of these shows. */}
              {tagLabel && (
                <span
                  className="pointer-events-none absolute right-0 top-0 flex items-center justify-center rounded-bl-md bg-background/85 p-1"
                  title={tagLabel}
                >
                  {isNameplate ? (
                    <Tag className="h-3 w-3 text-primary" />
                  ) : (
                    <QrCode className="h-3 w-3 text-primary" />
                  )}
                </span>
              )}
            </button>
          );
        })}
      </div>
      <PhotoViewerModal
        photos={viewerPhotos}
        initialIndex={viewerIndex}
        open={viewerOpen}
        onOpenChange={setViewerOpen}
        onRotateSave={handleRotateSave}
        onSetTag={handleSetTag}
        onDelete={handleDeletePhoto}
      />
    </div>
  );
}

function AssetEditorRow({
  asset,
  categories,
  propertyId,
  photos,
}: {
  asset: EditableAsset;
  categories: AssetCategoryOption[];
  propertyId: string;
  photos: SurveyAssetPhoto[];
}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [expanded, setExpanded] = useState(false);
  const [name, setName] = useState(asset.name);
  const [nameStatus, setNameStatus] = useState<FieldStatus>("idle");
  const [categoryStatus, setCategoryStatus] = useState<FieldStatus>("idle");
  const [subtypeStatus, setSubtypeStatus] = useState<FieldStatus>("idle");
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    setDeleting(true);
    // Soft-delete goes through an RPC: the restrictive RLS policy hides deleted
    // rows, and a plain UPDATE into that hidden state is rejected by PostgREST.
    const { error } = await supabase.rpc("soft_delete_property_asset", {
      p_id: asset.id,
    });

    if (error) {
      setDeleting(false);
      toast({
        title: "Failed to delete asset",
        description: error.message,
        variant: "destructive",
      });
      return;
    }

    toast({ title: "Asset moved to recycle bin" });
    await queryClient.invalidateQueries({ queryKey: ["editable-assets", propertyId] });
    await queryClient.invalidateQueries({ queryKey: ["survey-assets", propertyId] });
    await queryClient.invalidateQueries({ queryKey: ["recycled-assets", propertyId] });
  };

  // Subtype options come from the asset's currently-selected category.
  // Keep any existing value that isn't in the list so it still displays.
  const currentCategory = categories.find((c) => c.id === asset.category_id);
  const subtypeOptions = (() => {
    const opts = currentCategory?.descriptions ?? [];
    if (asset.description && !opts.includes(asset.description)) {
      return [asset.description, ...opts];
    }
    return opts;
  })();

  const saveField = async (
    updates: TablesUpdate<"property_assets">,
    setStatus: (s: FieldStatus) => void,
  ) => {
    setStatus("saving");
    const { error } = await supabase
      .from("property_assets")
      .update(updates)
      .eq("id", asset.id);

    if (error) {
      setStatus("error");
      toast({
        title: "Failed to save",
        description: error.message,
        variant: "destructive",
      });
      return;
    }

    await queryClient.invalidateQueries({
      queryKey: ["editable-assets", propertyId],
    });
    await queryClient.invalidateQueries({
      queryKey: ["survey-assets", propertyId],
    });
    setStatus("success");
    setTimeout(() => setStatus("idle"), 1500);
  };

  const handleNameBlur = () => {
    const trimmed = name.trim();
    if (trimmed === asset.name) {
      setName(trimmed); // normalize away stray whitespace so the input matches stored value
      return;
    }
    if (trimmed === "") {
      setName(asset.name); // name is NOT NULL — revert empty edits
      return;
    }
    saveField({ name: trimmed }, setNameStatus);
  };

  const handleSubtypeChange = (value: string) => {
    if (value === (asset.description ?? "")) return;
    saveField({ description: value }, setSubtypeStatus);
  };

  const handleCategoryChange = (categoryId: string) => {
    const cat = categories.find((c) => c.id === categoryId);
    if (!cat) return;
    saveField({ category_id: cat.id }, setCategoryStatus);
  };

  return (
    <Collapsible
      open={expanded}
      onOpenChange={setExpanded}
      className="rounded-md border border-border/40 bg-background/30"
    >
      <div className="grid grid-cols-1 md:grid-cols-[72px_1fr_280px_1fr_44px] gap-2 items-center p-1">
        <div className="flex items-center gap-1">
          <CollapsibleTrigger asChild>
            <button
              type="button"
              className="flex h-8 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-accent/40 hover:text-foreground transition-colors"
              aria-label={
                expanded
                  ? "Hide photos"
                  : `Show ${photos.length} photo${photos.length !== 1 ? "s" : ""}`
              }
            >
              <ChevronRight
                className={cn(
                  "h-4 w-4 transition-transform",
                  expanded && "rotate-90",
                )}
              />
            </button>
          </CollapsibleTrigger>
          {photos.length > 0 && (
            <Badge
              variant="secondary"
              className="h-5 gap-1 px-1.5 text-[10px] font-medium"
            >
              <ImageIcon className="h-2.5 w-2.5" />
              {photos.length}
            </Badge>
          )}
        </div>

        <FieldCell status={nameStatus}>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            onBlur={handleNameBlur}
            aria-label="Asset name"
          />
        </FieldCell>

        <FieldCell status={categoryStatus}>
          <Select
            value={asset.category_id ?? undefined}
            onValueChange={handleCategoryChange}
          >
            <SelectTrigger aria-label="Asset category">
              <SelectValue placeholder="Select category" />
            </SelectTrigger>
            <SelectContent>
              {categories.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FieldCell>

        <FieldCell status={subtypeStatus}>
          <Select
            value={asset.description ?? undefined}
            onValueChange={handleSubtypeChange}
            disabled={subtypeOptions.length === 0}
          >
            <SelectTrigger aria-label="Asset subtype">
              <SelectValue placeholder="Select subtype" />
            </SelectTrigger>
            <SelectContent>
              {subtypeOptions.map((d) => (
                <SelectItem key={d} value={d}>
                  {d}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FieldCell>

        <div className="flex items-center justify-center">
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <button
                type="button"
                className="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition-colors disabled:opacity-50"
                aria-label={`Delete ${asset.name}`}
                disabled={deleting}
              >
                {deleting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Trash2 className="h-4 w-4" />
                )}
              </button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete this asset?</AlertDialogTitle>
                <AlertDialogDescription>
                  "{asset.name}" will be moved to the recycle bin. You can restore
                  it from the Recycled section below.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={handleDelete}>Delete</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      <CollapsibleContent>
        <AssetPhotoStrip photos={photos} propertyId={propertyId} />
      </CollapsibleContent>
    </Collapsible>
  );
}

export function AssetsEditorSection({
  projectId,
  propertyId,
}: {
  projectId: string;
  propertyId: string;
}) {
  const { data: assets = [] } = useEditablePropertyAssets(propertyId);
  const { data: categories = [] } = useAssetCategories();
  const { data: assetsByCategory } = usePropertySurveyAssets(propertyId);
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [addAssetOpen, setAddAssetOpen] = useState(false);

  // Reuse the same already-cached photo data that powers the property's photo
  // count, keyed by asset id (covers both primary_image_path and asset_documents).
  const photosByAssetId = useMemo(() => {
    const m = new Map<string, SurveyAssetPhoto[]>();
    if (!assetsByCategory) return m;
    for (const group of Object.values(assetsByCategory)) {
      for (const photo of group.photos) {
        const arr = m.get(photo.asset_id);
        if (arr) arr.push(photo);
        else m.set(photo.asset_id, [photo]);
      }
    }
    return m;
  }, [assetsByCategory]);

  const categoryNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const c of categories) m.set(c.id, c.name);
    return m;
  }, [categories]);

  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { data: recycledRows = [] } = useRecycledPropertyAssets(propertyId);
  const { data: recycledPhotos = [] } = useRecycledAssetPhotos(propertyId);
  const [binViewerIndex, setBinViewerIndex] = useState<number | null>(null);

  // Deleted photos are still full-resolution originals, so the bin gets the same
  // 250 px thumbnail treatment as the grids.
  const binPhotos = useMemo(
    () => recycledPhotos.filter((p): p is typeof p & { file_path: string } => !!p.file_path),
    [recycledPhotos],
  );
  const { data: binThumbnails } = useThumbnailUrls(
    useMemo(() => binPhotos.map((p) => p.file_path), [binPhotos]),
  );
  // Row id -> position in binViewerPhotos, so a row without a file_path still
  // renders (and stays restorable) instead of vanishing from the bin. None exist
  // in prod today, but an unrestorable deleted photo is not a failure mode worth
  // leaving open.
  const binIndexById = useMemo(() => {
    const m = new Map<string, number>();
    binPhotos.forEach((p, i) => m.set(p.id, i));
    return m;
  }, [binPhotos]);

  const binViewerPhotos = useMemo<PhotoViewerPhoto[]>(
    () =>
      binPhotos.map((p) => ({
        url: p.file_path,
        thumbnailUrl: binThumbnails?.get(p.file_path),
        id: p.id,
      })),
    [binPhotos, binThumbnails],
  );

  const recycled = useMemo(
    () => recycledRows.map((r) => mapRecycledRow(r, categoryNameById)),
    [recycledRows, categoryNameById],
  );

  const handleRestore = async (id: string) => {
    // Restore also goes through an RPC: a deleted row is RLS-hidden, so a plain
    // UPDATE would match zero rows.
    const { error } = await supabase.rpc("restore_property_asset", { p_id: id });
    if (error) {
      toast({ title: "Failed to restore", description: error.message, variant: "destructive" });
      return;
    }
    toast({ title: "Asset restored" });
    await queryClient.invalidateQueries({ queryKey: ["recycled-assets", propertyId] });
    await queryClient.invalidateQueries({ queryKey: ["editable-assets", propertyId] });
    await queryClient.invalidateQueries({ queryKey: ["survey-assets", propertyId] });
  };

  const assetNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const a of assets) m.set(a.id, a.name);
    return m;
  }, [assets]);

  const handleRestorePhoto = async (id: string) => {
    try {
      await restoreAssetPhoto(id);
    } catch (err) {
      toast({
        title: "Failed to restore photo",
        description: (err as Error).message,
        variant: "destructive",
      });
      return;
    }
    toast({ title: "Photo restored" });
    await queryClient.invalidateQueries({ queryKey: ["recycled-asset-photos", propertyId] });
    await queryClient.invalidateQueries({ queryKey: ["survey-assets", propertyId] });
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const result = assets.filter((a) => {
      if (categoryFilter !== "all" && a.category_id !== categoryFilter) {
        return false;
      }
      if (q && !a.name.toLowerCase().includes(q)) {
        return false;
      }
      return true;
    });

    // Sort by category -> subtype -> name so related rows sit together.
    // Empty category/subtype sort to the end.
    const sortKey = (value: string) =>
      value ? value.toLowerCase() : "￿";
    const catName = (a: EditableAsset) =>
      (a.category_id && categoryNameById.get(a.category_id)) || a.category || "";

    return result.sort((a, b) => {
      const byCat = sortKey(catName(a)).localeCompare(sortKey(catName(b)));
      if (byCat !== 0) return byCat;
      const bySub = sortKey(a.description ?? "").localeCompare(
        sortKey(b.description ?? ""),
      );
      if (bySub !== 0) return bySub;
      return a.name.localeCompare(b.name);
    });
  }, [assets, search, categoryFilter, categoryNameById]);

  return (
    <Collapsible defaultOpen={false}>
      <Card className="border border-border/50 overflow-hidden bg-card/50 backdrop-blur-sm">
        <CollapsibleTrigger asChild>
          <button className="w-full flex items-center justify-between p-4 hover:bg-accent/30 transition-colors text-left">
            <div className="flex items-center gap-2">
              <Pencil className="h-4 w-4 text-primary" />
              <span className="font-medium text-base text-foreground">
                Edit Assets
              </span>
            </div>
            <Badge variant="secondary" className="text-xs">
              {assets.length} asset{assets.length !== 1 ? "s" : ""}
            </Badge>
          </button>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <CardContent className="pt-0 pb-3 px-4 space-y-3">
            {/* Search + category filter + add */}
            <div className="flex flex-col sm:flex-row gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search assets by name..."
                  className="pl-8"
                />
              </div>
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="sm:w-56" aria-label="Filter by category">
                  <SelectValue placeholder="All categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All categories</SelectItem>
                  {categories.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                type="button"
                variant="outline"
                className="gap-2 sm:w-auto"
                onClick={() => setAddAssetOpen(true)}
              >
                <Plus className="h-4 w-4" />
                Add asset
              </Button>
            </div>

            {/* Column headers (md+) */}
            <div className="hidden md:grid grid-cols-[72px_1fr_280px_1fr_44px] gap-2 px-1 text-xs font-medium text-muted-foreground">
              <span />
              <span>Name</span>
              <span>Category</span>
              <span>Subtype</span>
              <span />
            </div>

            {/* Rows */}
            <div className="space-y-2">
              {filtered.map((asset) => (
                <AssetEditorRow
                  key={asset.id}
                  asset={asset}
                  categories={categories}
                  propertyId={propertyId}
                  photos={photosByAssetId.get(asset.id) ?? []}
                />
              ))}
              {filtered.length === 0 && (
                <p className="text-sm text-muted-foreground py-2 px-1">
                  {assets.length === 0
                    ? "No assets recorded for this property yet."
                    : "No assets match the current filters."}
                </p>
              )}
            </div>

            {/* Recycle bin */}
            <Collapsible className="mt-2 rounded-md border border-border/40 bg-background/20">
              <CollapsibleTrigger asChild>
                <button
                  type="button"
                  className="w-full flex items-center justify-between p-3 text-left hover:bg-accent/20 transition-colors"
                >
                  <span className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                    <Trash className="h-4 w-4" />
                    Recycled
                  </span>
                  <Badge variant="secondary" className="text-xs">
                    {recycled.length + recycledPhotos.length}
                  </Badge>
                </button>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="space-y-1 p-3 pt-0">
                  {recycled.length === 0 && recycledPhotos.length === 0 ? (
                    <p className="text-sm text-muted-foreground py-1">
                      No deleted assets or photos.
                    </p>
                  ) : (
                    recycled.map((r) => (
                      <div
                        key={r.id}
                        className="flex items-center justify-between gap-2 rounded-md border border-border/30 px-3 py-2"
                      >
                        <div className="min-w-0">
                          <p className="truncate text-sm text-foreground">{r.name}</p>
                          <p className="truncate text-xs text-muted-foreground">
                            {r.category ?? "Uncategorized"}
                            {r.deletedAt
                              ? ` · deleted ${new Date(r.deletedAt).toLocaleDateString()}`
                              : ""}
                          </p>
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="gap-1.5 flex-shrink-0"
                          onClick={() => handleRestore(r.id)}
                        >
                          <RotateCcw className="h-3.5 w-3.5" />
                          Restore
                        </Button>
                      </div>
                    ))
                  )}

                  {recycledPhotos.map((p) => (
                    <div
                      key={p.id}
                      className="flex items-center justify-between gap-2 rounded-md border border-border/30 px-3 py-2"
                    >
                      <div className="flex min-w-0 items-center gap-2">
                        {p.file_path ? (
                          <button
                            type="button"
                            onClick={() => setBinViewerIndex(binIndexById.get(p.id) ?? 0)}
                            className="h-10 w-10 flex-shrink-0 overflow-hidden rounded border border-border/60 transition-all hover:border-primary hover:ring-2 hover:ring-primary/30 focus:outline-none focus-visible:border-primary focus-visible:ring-2 focus-visible:ring-primary/30"
                            aria-label={`Preview ${p.document_name || "deleted photo"}`}
                          >
                            <FirebaseImage
                              src={binThumbnails?.get(p.file_path) ?? p.file_path}
                              alt={p.document_name || "Deleted photo"}
                              loading="lazy"
                              className="h-full w-full object-cover opacity-70"
                            />
                          </button>
                        ) : (
                          <ImageIcon className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />
                        )}
                        <div className="min-w-0">
                          <p className="truncate text-sm text-foreground">
                            {p.document_name || "Photo"}
                          </p>
                          <p className="truncate text-xs text-muted-foreground">
                            {assetNameById.get(p.asset_id) ?? "Unknown asset"}
                            {p.deleted_at
                              ? ` · deleted ${new Date(p.deleted_at).toLocaleDateString()}`
                              : ""}
                          </p>
                        </div>
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="gap-1.5 flex-shrink-0"
                        onClick={() => handleRestorePhoto(p.id)}
                      >
                        <RotateCcw className="h-3.5 w-3.5" />
                        Restore
                      </Button>
                    </div>
                  ))}
                </div>

                {/* Read-only preview: passing no callbacks hides the rotate,
                    tag and delete controls, which don't apply to a deleted
                    photo. */}
                <PhotoViewerModal
                  photos={binViewerPhotos}
                  initialIndex={binViewerIndex ?? 0}
                  open={binViewerIndex !== null}
                  onOpenChange={(next) => {
                    if (!next) setBinViewerIndex(null);
                  }}
                />
              </CollapsibleContent>
            </Collapsible>
          </CardContent>
        </CollapsibleContent>
      </Card>

      <AddAssetDialog
        projectId={projectId}
        propertyId={propertyId}
        categories={categories}
        open={addAssetOpen}
        onOpenChange={setAddAssetOpen}
      />
    </Collapsible>
  );
}
